import { createAsyncThunk } from '@reduxjs/toolkit'

import {
  buildEssentialsEventsQuery,
  ESSENTIALS_ENTRY_TAG,
  ESSENTIALS_GET_EVENTS_QUERY,
  ESSENTIALS_SITE_CATEGORY,
  EssentialsEventData,
  EssentialsEventDataApiResponse,
  EssentialsRegisteredEventData,
  formatEssentialsEventData,
  getEssentialsAppEnv,
  getEssentialsCmsAxiosInstance,
} from '@lyrahealth-inc/shared-app-logic'

import { axiosInstance } from '../../common/http/axiosInstance'

export const GET_REGISTERED_ESSENTIALS_EVENTS_FOR_USER = 'GET_REGISTERED_ESSENTIALS_EVENTS_FOR_USER'
export const FETCH_ESSENTIALS_EVENTS_BY_TAGS = 'FETCH_ESSENTIALS_EVENTS_BY_TAGS'
export const FETCH_ESSENTIALS_EVENTS_FULL_LIST = 'FETCH_ESSENTIALS_EVENTS_FULL_LIST'
export const SET_SHOULD_GO_FULLSCREEN_FOR_MIXES = 'SET_SHOULD_GO_FULLSCREEN_FOR_MIXES'

/**
 * Gets a list of essentials events that the user has registered for
 */
export const getRegisteredEssentialsEventsForUser = createAsyncThunk<
  EssentialsRegisteredEventData[],
  { lyraId: string }
>(GET_REGISTERED_ESSENTIALS_EVENTS_FOR_USER, async ({ lyraId }: { lyraId: string }) => {
  try {
    const response = await axiosInstance.get(`/v1/essentials/events/${lyraId}`)
    const result: EssentialsRegisteredEventData[] = Array.isArray(response.data) ? response.data : []
    result.forEach((event: EssentialsRegisteredEventData) => {
      event.timeStamp.eventDate = new Date(event.timeStamp.eventDate)
    })
    return result
  } catch (e) {
    console.error(e)
    return []
  }
})

/**
 * Fetches a list essentials events that have matching tag(s)
 */
export const fetchEssentialsEventsByTags = createAsyncThunk<
  EssentialsEventData[],
  { essentialsCmsUrl?: string; tags: ESSENTIALS_ENTRY_TAG[] }
>(
  FETCH_ESSENTIALS_EVENTS_BY_TAGS,
  async ({ essentialsCmsUrl, tags }: { essentialsCmsUrl?: string; tags: ESSENTIALS_ENTRY_TAG[] }) => {
    const query = buildEssentialsEventsQuery({ includeTags: true })
    const data = {
      query,
      variables: {
        site: 'en',
        siteCategory: ESSENTIALS_SITE_CATEGORY.ESSENTIALS,
        tags,
      },
    }

    try {
      if (!essentialsCmsUrl) {
        throw new Error('essentialsCmsUrl is empty')
      }
      const instance = getEssentialsCmsAxiosInstance(essentialsCmsUrl)
      const response = await instance.post('?getEventsByTags=true', data)

      if (response.status !== 200) {
        throw new Error('Invalid response for getEventsByTags')
      } else {
        const responseData: EssentialsEventDataApiResponse[] = response.data?.data?.entries
        const finalEvents = formatEssentialsEventData(responseData)
        return finalEvents
      }
    } catch (e) {
      console.error(`Failed to fetch essentials events w/tags (${tags.join(', ')})` + e)
      return []
    }
  },
)

/**
 * Fetches a complete list of all essentials events that exist
 */
export const fetchEssentialsEventsFullList = createAsyncThunk<EssentialsEventData[], { essentialsCmsUrl?: string }>(
  FETCH_ESSENTIALS_EVENTS_FULL_LIST,
  async ({ essentialsCmsUrl }: { essentialsCmsUrl?: string }) => {
    const data = {
      query: ESSENTIALS_GET_EVENTS_QUERY,
      variables: {
        site: 'en',
        appEnv: getEssentialsAppEnv(),
      },
    }

    try {
      if (!essentialsCmsUrl) {
        throw new Error('essentialsCmsUrl is empty')
      }
      const instance = getEssentialsCmsAxiosInstance(essentialsCmsUrl)
      const response = await instance.post('?getEvents=true', data) // getEvents param added in order to distinguish this call in the ci tests for mocking, since all essentials graphql calls hit the same endpoint. Not used by goodness.

      if (response.status !== 200) {
        throw new Error('Invalid response for getEvents')
      } else {
        const responseData: EssentialsEventDataApiResponse[] = response.data?.data?.entries
        const finalEvents = formatEssentialsEventData(responseData)
        return finalEvents
      }
    } catch (e) {
      console.error('Failed to fetch essentials events ' + e)
      return []
    }
  },
)

export const GET_REGISTERED_ESSENTIALS_EVENTS_FOR_USER_fulfilled = getRegisteredEssentialsEventsForUser.fulfilled.type
export const GET_REGISTERED_ESSENTIALS_EVENTS_FOR_USER_pending = getRegisteredEssentialsEventsForUser.pending.type
export const GET_REGISTERED_ESSENTIALS_EVENTS_FOR_USER_rejected = getRegisteredEssentialsEventsForUser.rejected.type

export const FETCH_ESSENTIALS_EVENTS_BY_TAGS_fulfilled = fetchEssentialsEventsByTags.fulfilled.type
export const FETCH_ESSENTIALS_EVENTS_BY_TAGS_pending = fetchEssentialsEventsByTags.pending.type
export const FETCH_ESSENTIALS_EVENTS_BY_TAGS_rejected = fetchEssentialsEventsByTags.rejected.type

export const FETCH_ESSENTIALS_EVENTS_FULL_LIST_fulfilled = fetchEssentialsEventsFullList.fulfilled.type
export const FETCH_ESSENTIALS_EVENTS_FULL_LIST_pending = fetchEssentialsEventsFullList.pending.type
export const FETCH_ESSENTIALS_EVENTS_FULL_LIST_rejected = fetchEssentialsEventsFullList.rejected.type

export const setShouldGoFullscreenForMixes = (value: boolean) => ({
  type: SET_SHOULD_GO_FULLSCREEN_FOR_MIXES,
  value,
})
